import React, { useState, useEffect } from 'react';
import { DEFAULT_BRANDING } from '../constants';

interface BrandLogoProps {
  src: string | undefined;
  alt?: string;
  className?: string;
  fallbackSrc?: string;
}

/**
 * Ajoute un cache-buster aux URLs externes pour éviter le cache navigateur
 */
const addCacheBuster = (url: string): string => {
  // Ne pas ajouter aux URLs locales/relatives
  if (!url || url.startsWith('./') || url.startsWith('/')) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${Date.now()}`;
};

/**
 * Composant logo avec fallback automatique vers le logo par défaut
 * si l'image ne charge pas (URL invalide, erreur réseau, etc.)
 */
export function BrandLogo({
  src,
  alt = 'Logo',
  className = '',
  fallbackSrc = DEFAULT_BRANDING.logo1
}: BrandLogoProps) {
  const [imgSrc, setImgSrc] = useState(src ? addCacheBuster(src) : fallbackSrc);
  const [hasError, setHasError] = useState(false);

  // Synchroniser imgSrc quand src change
  useEffect(() => {
    if (src) {
      setImgSrc(addCacheBuster(src));
      setHasError(false);
    }
  }, [src]);

  const handleError = () => {
    if (!hasError && imgSrc !== fallbackSrc) {
      setHasError(true);
      setImgSrc(fallbackSrc);
    }
  };

  // Si pas de src et c'est un logo optionnel (logo2), ne rien afficher
  if (!src && !fallbackSrc) {
    return null;
  }

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={handleError}
    />
  );
}
